import { observer } from 'mobx-react-lite';
import type { SensorAlertBundle } from '@mark43/cad-api';
import { Button, useDisclosure } from '@cad-arc';
import { AttachToEventModal } from './attach-to-event-modal';
import { getLinkedEventIds } from './use-link-sensor-alerts';

/**
 * Opens the event picker, and reflects how many events this alert is already attached to.
 *
 * Always enabled. An earlier version offered only the events already open as panels, because the
 * endpoint takes a numeric event id and those were the ids this client held — but the app's own
 * `EventAutocomplete` returns `agencyEventId` directly, so there is no reason to restrict it. A
 * sensor event is often relevant to an event nobody has open.
 */
export const AttachToEventButton = observer(function AttachToEventButton({
    alert,
    size,
}: {
    alert: SensorAlertBundle;
    size?: 'sm';
}): React.ReactElement {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const linkedCount = getLinkedEventIds(alert).length;

    return (
        <>
            <Button size={size} onClick={onOpen}>
                {linkedCount > 0
                    ? `Attached to ${linkedCount} event${linkedCount === 1 ? '' : 's'}`
                    : 'Attach to event…'}
            </Button>
            {/* Mounted only while open, so a list of rows does not carry a live autocomplete each. */}
            {isOpen ? (
                <AttachToEventModal alert={alert} isOpen={isOpen} onClose={onClose} />
            ) : undefined}
        </>
    );
});
